import React, { ReactElement, FunctionComponent, memo } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import duration from 'dayjs/plugin/duration'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import Grid from '@sport1/react-elements/Grid'
import BroadcastCard from '../BroadcastCard'
import { BroadcastCallback, TVChannel } from '../types'
import { BROADCAST_WIDTH_MULTIPLICATOR } from '..'
import { formatServerTimeToDayjs } from '../utils/dayjsHelper'

type ChannelRowType = {
    channel?: TVChannel
    channelStartTime: Dayjs
    channelEndTime: Dayjs
    onPress?: (channel: BroadcastCallback) => void
    href?: string
}

const diffInMinutes = (left: Dayjs, right: Dayjs): number =>
    Math.floor(dayjs.duration(right.diff(left)).asMinutes())

const ChannelRow: FunctionComponent<ChannelRowType> = ({
    channel,
    channelStartTime,
    channelEndTime,
    onPress,
    href,
}): ReactElement => {
    dayjs.extend(duration)
    dayjs.extend(customParseFormat)
    return (
        <Grid noExpand horizontal>
            {channel?.broadcasts.map((broadcast, index) => {
                const broadcastCard = (
                    <BroadcastCard
                        key={`${broadcast.startTime}_${broadcast.programTitle}`}
                        width={
                            diffInMinutes(
                                formatServerTimeToDayjs(broadcast.startTime),
                                formatServerTimeToDayjs(broadcast.endTime)
                            ) * BROADCAST_WIDTH_MULTIPLICATOR
                        }
                        programTitle={broadcast.programTitle}
                        episodeTitle={broadcast.episodeTitle}
                        startTime={broadcast.startTime}
                        endTime={broadcast.endTime}
                        emissionType={broadcast.emissionType}
                        onPress={onPress}
                        href={href}
                        channelId={channel.id}
                        channelName={channel.name}
                        streamingUrl={channel.streamingUrl}
                    />
                )
                if (index === 0) {
                    const startTimeDiff = diffInMinutes(
                        channelStartTime,
                        formatServerTimeToDayjs(broadcast.startTime)
                    )
                    return (
                        <Grid
                            key={`${broadcast.startTime}_${broadcast.programTitle}_start`}
                            noExpand
                            horizontal
                        >
                            {startTimeDiff ? (
                                <Grid
                                    noExpand
                                    width={startTimeDiff * BROADCAST_WIDTH_MULTIPLICATOR}
                                    backgroundColor="pearl"
                                    borderRight={1}
                                    borderTop={1}
                                    borderColor="tahitian-4"
                                />
                            ) : null}
                            {broadcastCard}
                        </Grid>
                    )
                }
                if (index === channel?.broadcasts.length - 1) {
                    const endTimeDiff = diffInMinutes(
                        formatServerTimeToDayjs(broadcast.endTime),
                        channelEndTime
                    )
                    return (
                        <Grid
                            key={`${broadcast.startTime}_${broadcast.programTitle}_end`}
                            noExpand
                            horizontal
                        >
                            {broadcastCard}
                            {endTimeDiff ? (
                                <Grid
                                    noExpand
                                    width={endTimeDiff * BROADCAST_WIDTH_MULTIPLICATOR}
                                    backgroundColor="pearl"
                                    borderTop={1}
                                    borderColor="tahitian-4"
                                />
                            ) : null}
                        </Grid>
                    )
                }
                return broadcastCard
            })}
        </Grid>
    )
}

export default memo(ChannelRow)
